

import { IconArrowBackUp, IconDeviceFloppy } from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslations } from "next-intl";
import { usePreviewWriteGuard } from "@/lib/preview/usePreviewWriteGuard";

type EditingProps = {
  changes: boolean;
  handleSaveAction: () => void;
  handleUndoAction: () => void;
};

export const Editing: React.FC<EditingProps> = ({
  changes,
  handleSaveAction,
  handleUndoAction,
}) => {
  const t = useTranslations("guestDetails");
  // Read-only preview: explain instead of firing the masked update.
  const { guard } = usePreviewWriteGuard();
  return (
    <AnimatePresence>
      <motion.div
        className="inline-flex items-center surface-primary rounded-lg border outline-quaternary mr-3 sm:mr-6"
        initial={{ opacity: 0, scale: 0.8, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8, y: 10 }}
        transition={{ type: "spring", stiffness: 500, damping: 30 }}
      >
        <button
          onClick={handleUndoAction}
          className={`${changes ? "" : "opacity-10 cursor-default"} p-2 `}
          disabled={!changes}
          aria-label={t("undoChanges")}
        >
          {changes ? (
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              animate={{ rotate: [0, -20, 0] }}
              transition={{
                duration: 0.5,
                ease: "easeInOut",
                times: [0, 0.6, 1],
                repeat: 0,
                repeatDelay: 0.5,
              }}
            >
              <IconArrowBackUp size={18} stroke={1.5} />
            </motion.div>
          ) : (
            <div>
              <IconArrowBackUp size={18} stroke={1.5} />
            </div>
          )}
        </button>

        <motion.div
          className="h-6 mx-1 w-px surface-outline-quaternary"
          initial={{ height: 0 }}
          animate={{ height: 24 }}
          transition={{ delay: 0.2, duration: 0.3 }}
        />

        <button
          onClick={guard(handleSaveAction)}
          className={`${changes ? "" : "opacity-10 cursor-default"} p-2`}
          disabled={!changes}
          aria-label={t("saveChanges")}
        >
          {changes ? (
            <motion.div
              whileHover={{
                scale: 1.1,
                y: [0, -2, 0],
                transition: {
                  y: {
                    repeat: Infinity,
                    repeatType: "mirror",
                    duration: 0.5,
                  },
                },
              }}
              whileTap={{ scale: 0.95 }}
            >
              <IconDeviceFloppy size={18} stroke={1.5} />
            </motion.div>
          ) : (
            <div>
              <IconDeviceFloppy size={18} stroke={1.5} />
            </div>
          )}
        </button>
      </motion.div>
    </AnimatePresence>
  );
};
